/**
 * Hardware Unlocks hook using React Relay
 *
 * Fetches a hardware unit's unlock attempt/success events for a trailing
 * window (last 30 days), along with the backend-computed unlock success
 * rate over the same window.
 *
 * This is intentionally a separate query from useHardwareDetailRelay: a
 * backend error in the unlocks calculations nulls out the whole
 * getHardware payload, and keeping the fields isolated means such a
 * failure only degrades the Unlocks tab instead of blanking the entire
 * hardware detail page.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { IUnlockEventJson } from '../../../../util/unlockEvents';
import type { useHardwareUnlocksRelayQuery as UseHardwareUnlocksRelayQuery } from './__generated__/useHardwareUnlocksRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const HardwareUnlocksQuery = graphql`
  query useHardwareUnlocksRelayQuery(
    $id: ID!
    $startTime: DateTime!
    $endTime: DateTime!
    $days: Int
  ) {
    getHardware(id: $id) {
      id
      unlocks(startTime: $startTime, endTime: $endTime)
      unlockSuccessRate(days: $days)
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/** Trailing window shown on the hardware Unlocks tab */
export const HARDWARE_UNLOCKS_WINDOW_DAYS = 30;

/**
 * Hardware unlocks payload from GraphQL
 */
export interface HardwareUnlocksGraphQL {
  id: string;
  /** Unlock events in the requested window, oldest first (Json, pre-parsed) */
  unlocks?: readonly IUnlockEventJson[] | null;
  /**
   * Percentage (0-100) of attempts in the trailing window with a success
   * recorded between the backward window before the attempt and the unlock
   * window after it; null when there were no attempts
   */
  unlockSuccessRate?: number | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook that provides a React Query-like interface for a hardware unit's
 * unlock events over the last HARDWARE_UNLOCKS_WINDOW_DAYS days. Fetches
 * as soon as it is enabled so the data (and the failed unlock count shown
 * on the Unlocks tab badge) is ready before the user visits the tab.
 *
 * @param hardwareId - The hardware's GraphQL UUID
 * @param options.enabled - Whether to enable the query (e.g. gate on the
 *   hardware product supporting remote/BLE unlock)
 *
 * @example
 * const { data, isLoading, error } = useHardwareUnlocksRelayQuery(hardwareId, {
 *   enabled: supportsUnlock,
 * });
 */
export function useHardwareUnlocksRelayQuery(
  hardwareId: string | undefined,
  options?: {
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<HardwareUnlocksGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(hardwareId);

  useEffect(() => {
    if (!enabled || !hardwareId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const endTime = new Date();
        const startTime = new Date(
          endTime.getTime() - HARDWARE_UNLOCKS_WINDOW_DAYS * 24 * 60 * 60 * 1000
        );

        const result = await fetchQuery<UseHardwareUnlocksRelayQuery>(
          relayEnvironment,
          HardwareUnlocksQuery,
          {
            id: hardwareId,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            days: HARDWARE_UNLOCKS_WINDOW_DAYS,
          }
        ).toPromise();

        if (cancelled) return;

        setData((result?.getHardware as HardwareUnlocksGraphQL | null) ?? null);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch hardware unlocks')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, hardwareId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      error,
      refetch,
    }),
    [data, isLoading, error, refetch]
  );
}
